import styled from 'styled-components';

import { ActionMenu } from 'ui/actionMenu';
import {
  type ActionMenuOption,
  AllowedActionMenuElements,
  MENU_ALIGNMENTS,
} from 'ui/actionMenu/types';
import { SecondaryButton } from 'ui/button';
import { Overflow } from 'ui/icons/navigation/Overflow';
import { Delete16 } from 'ui/icons/aurora/16px/Delete16';
import { Edit16 } from 'ui/icons/action/Edit16';
import { Open16 } from 'ui/icons/aurora/16px/Open16';
import { MakeCopy16 } from 'ui/icons/aurora/16px/MakeCopy16';
import { MoveToFolder } from 'ui/icons/action/MoveToFolder';
import { colors, spacing } from 'ui/theme';
import { useSelectionContext } from 'modules/creativeLibrary/contexts/SelectionContext';
import { useCreativeLibraryFolderModals } from 'modules/creativeLibrary/hooks/useCreativeLibraryModals';
import type { Asset } from 'modules/creativeLibrary/types';
import { useUploadAssetsContext } from 'modules/creativeLibrary/contexts/UploadAssetsContext';
import { updateClipboard } from 'utils/clipboard';
import { useDispatch } from 'react-redux';
import { addPositiveToast } from 'modules/toastContainer';

type Props = {
  asset: Asset;
  isPanel: boolean;
  hasManageBaseTemplatesPermission: boolean;
};

const StyledButton = styled(SecondaryButton)`
  padding: 0 ${spacing.half};
`;

export const displayContent = {
  move: 'Move to folder',
  edit: 'Edit image details',
  copyUrl: 'Copy image URL',
  fullSize: 'View full size image',
  delete: 'Delete image',
};

export const AssetActionMenu = ({ asset, isPanel, hasManageBaseTemplatesPermission }: Props) => {
  const dispatch = useDispatch();
  const { selectItem } = useSelectionContext();
  const { onEditFiles } = useUploadAssetsContext();
  const modals = useCreativeLibraryFolderModals();

  const options: ActionMenuOption[] =
    !hasManageBaseTemplatesPermission || isPanel
      ? [
          {
            as: AllowedActionMenuElements.Button,
            content: (
              <>
                <MakeCopy16 />
                <span>{displayContent.copyUrl}</span>
              </>
            ),
            id: 'copy-image-url',
            onClick: async () => {
              await updateClipboard(asset.url);
              dispatch(addPositiveToast(`Copied ${asset.assetName} image url.`));
            },
          },
          {
            as: AllowedActionMenuElements.Link,
            content: (
              <>
                <Open16 />
                <span>{displayContent.fullSize}</span>
              </>
            ),
            href: asset.url,
            isExternalLink: true,
            id: 'view-full-size-image',
          },
        ]
      : [
          {
            as: AllowedActionMenuElements.Button,
            content: (
              <>
                <MoveToFolder color={colors.grey50} height={16} width={16} />
                <span>{displayContent.move}</span>
              </>
            ),
            id: 'move-to-folder',
            onClick: () => {
              selectItem({ ...asset, name: asset.assetName });
              modals.move.toggle(true);
            },
          },
          {
            as: AllowedActionMenuElements.Button,
            content: (
              <>
                <Edit16 />
                <span>{displayContent.edit}</span>
              </>
            ),
            id: 'edit-image-details',
            onClick: () => {
              onEditFiles([asset.id]);
            },
          },
          {
            as: AllowedActionMenuElements.Button,
            content: (
              <>
                <MakeCopy16 />
                <span>{displayContent.copyUrl}</span>
              </>
            ),
            id: 'copy-image-url',
            onClick: async () => {
              await updateClipboard(asset.url);
              dispatch(addPositiveToast(`Copied ${asset.assetName} image url.`));
            },
          },
          {
            as: AllowedActionMenuElements.Link,
            content: (
              <>
                <Open16 />
                <span>{displayContent.fullSize}</span>
              </>
            ),
            href: asset.url,
            isExternalLink: true,
            id: 'view-full-size-image',
          },
          {
            as: AllowedActionMenuElements.Button,
            isDestructive: true,
            content: (
              <>
                <Delete16 />
                <span>{displayContent.delete}</span>
              </>
            ),
            id: 'delete-asset',
            onClick: () => {
              selectItem({ ...asset, name: asset.assetName });
              modals.delete.toggle(true);
            },
          },
        ];

  return (
    <ActionMenu align={MENU_ALIGNMENTS.RIGHT} id={`action-menu-${asset.id}`} options={options}>
      <StyledButton>
        <Overflow color={colors.grey90} />
      </StyledButton>
    </ActionMenu>
  );
};
